import { toast } from 'sonner';
import { z } from 'zod';

import { SUPABASE_UPLOADS_BUCKET_NAME } from '../constants';

import { Env } from '@/lib/env';
import { generateUploadFilePath } from '@/lib/files/upload-file-path';
import { createClient } from '@/utils/supabase/client';

/** Allowed MIME types for uploaded media (images & videos) */
const ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'video/mp4',
  'video/webm',
  'video/quicktime',
] as const; // Update ALLOWED_VIDEO_FILE_EXTENSIONS if ever supporting more video formats
export const ALLOWED_VIDEO_FILE_EXTENSIONS = ['mp4', 'webm', 'mov'] as const;

/** Maximum allowed file size (in bytes) */
const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024; // 25MB

const FileSchema = z
  .instanceof(Blob)
  .refine((file) => file.size <= MAX_FILE_SIZE_BYTES, {
    message: 'File size should be less than 25MB',
  })
  .refine((file) => ALLOWED_MIME_TYPES.includes(file.type as (typeof ALLOWED_MIME_TYPES)[number]), {
    message: 'File type should be JPEG, PNG, MP4, WebM, or MOV',
  });
type ValidFile = z.infer<typeof FileSchema>;

/**
 * Validates a file against the defined schema,
 * handles errors and displays a toast with proper error messages.
 *
 * @param file - The file to validate
 * @returns The validated file if it passes all checks, or null if validation fails
 */
export const validateFile = (file: File): ValidFile | null => {
  try {
    return FileSchema.parse(file);
  } catch (error) {
    console.error('File validation failed:', error);
    if (error instanceof Error) {
      if (error.message === 'File validation failed') {
        toast.error('File validation failed');
      } else if (error instanceof z.ZodError) {
        // Check if the error is related to file size
        const sizeError = error.issues.find((e) => e.message.includes('size'));
        // Check if the error is related to file type
        const fileTypeError = error.issues.some((e) => e.message.includes('type'));

        if (sizeError) {
          toast.error('File size must be less than 25MB');
        } else if (fileTypeError) {
          toast.error('File type must be JPEG, PNG, MP4, WebM, or MOV');
        } else {
          toast.error('File validation failed');
        }
      }
    }
    return null;
  }
};

/**
 * Uploads a file to Supabase Storage and returns the public URL.
 *
 * This function:
 * - Generates a unique filename to prevent conflicts
 * - Uploads the file to the specified repository's folder
 * - Returns the public URL for accessing the uploaded file
 *
 * @param file - The file to upload
 * @param repoId - The ID of the repository to upload the file to
 * @returns Promise resolving to the public URL of the uploaded file
 */
export async function uploadFile(file: File, repoId: number): Promise<string> {
  const supabase = createClient();
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('Not authenticated');
  }
  if (!file || !repoId) {
    throw new Error('Invalid file or repository ID');
  }

  // Validate the file
  FileSchema.parse(file);

  // Generate a unique filename to prevent conflicts,
  // ensure proper path structure and make path unguessable
  const path = generateUploadFilePath(repoId, file);

  // owner of the file is the authenticated user
  const { error } = await supabase.storage
    .from(SUPABASE_UPLOADS_BUCKET_NAME)
    .upload(path, file, { cacheControl: '3600', upsert: false });
  if (error) {
    throw error;
  }

  // Generate the public URL for the uploaded file
  const baseUrl =
    process.env.NEXT_PUBLIC_VERCEL_ENV === 'preview'
      ? `https://${process.env.NEXT_PUBLIC_VERCEL_BRANCH_URL}`
      : Env.NEXT_PUBLIC_SITE_URL;
  const url = `${baseUrl}/api/files/${path}`;

  return url;
}
